import * as React from 'react';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { STALE_DATA_TIMEOUT } from '@studio/constants/api-constants';
import { useClient } from '@studio/hooks';
import { runCachedAbortableStoreFetch } from '@studio/storeV2/helpers/run-cached-abortable-store-fetch';
import { usersAtom, usersLoadInFlightAtom } from '../../users';

type _Props = {
  fetchListOnMount?: boolean;
};

const useUsers = ({ fetchListOnMount = false }: _Props = {}) => {
  const usersState = useAtomValue(usersAtom);
  const usersLoadInFlight = useAtomValue(usersLoadInFlightAtom);

  const { callApi: getUsers, isFetching: isClientFetching } = useClient({
    config: useClient.central.v3.users.GET,
  });

  const fetchUsers = useAtomCallback(
    React.useCallback(
      (get, set, options?: { forceRefresh?: boolean }) =>
        runCachedAbortableStoreFetch(get, set, {
          dataAtom: usersAtom,
          emptyOnError: [],
          fetchWithSignal: (signal) => getUsers(undefined, { signal }),
          forceRefresh: options?.forceRefresh,
          loadInFlightAtom: usersLoadInFlightAtom,
          staleTimeoutMs: STALE_DATA_TIMEOUT,
        }),
      [getUsers]
    )
  );

  React.useEffect(() => {
    if (!fetchListOnMount) {
      return;
    }
    void fetchUsers();
  }, [fetchListOnMount, fetchUsers]);

  return {
    error: usersState.error,
    fetchUsers,
    hasFetched: usersState.hasFetched,
    isFetching: usersLoadInFlight != null || isClientFetching,
    users: usersState.data,
  };
};

export default useUsers;
